import Icon from '@/components/ui/AppIcon';

interface Milestone {
  title: string;
  description: string;
  timeline: string;
}

interface PhaseCardProps {
  phase: {
    number: number;
    title: string;
    duration: string;
    description: string;
    keyObjectives: string[];
    milestones: Milestone[];
    investmentRange: string;
    expectedImpact: string;
  };
}

export default function PhaseCard({ phase }: PhaseCardProps) {
  return (
    <div className="bg-card rounded-lg shadow-institutional p-4 sm:p-6 border border-border hover:shadow-lg transition-shadow duration-300">
      <div className="flex items-start justify-between mb-2 sm:mb-4">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <div className="flex items-center justify-center w-10 h-8 bg-primary rounded-lg">
            <span className="font-headline text-2xl font-semibold text-primary-foreground">
              {phase.number}
            </span>
          </div>
          <div>
            <h3 className="font-headline text-xl sm:text-2xl font-bold text-foreground mb-1">
              {phase.title}
            </h3>
            <p className="font-body text-sm text-muted-foreground">{phase.duration}</p>
          </div>
        </div>
      </div>

      <p className="font-body text-base text-text-secondary leading-relaxed mb-2 sm:mb-4">
        {phase.description}
      </p>

      <div className="mb-2 sm:mb-4">
        <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
          Key Objectives
        </h4>
        <ul className="space-y-2">
          {phase.keyObjectives.map((objective, index) => (
            <li key={index} className="flex items-start space-x-3">
              <Icon
                name="CheckCircleIcon"
                size={20}
                variant="solid"
                className="text-success mt-0.5 flex-shrink-0"
              />
              <span className="font-body text-sm text-text-primary">{objective}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-2 sm:mb-4">
        <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
          Major Milestones
        </h4>
        <div className="space-y-2 sm:space-y-4">
          {phase.milestones.map((milestone, index) => (
            <div key={index} className="pl-4 border-l-2 border-accent">
              <div className="flex items-center justify-between mb-1">
                <h5 className="font-body text-sm font-medium text-foreground">
                  {milestone.title}
                </h5>
                <span className="font-body text-xs text-muted-foreground">
                  {milestone.timeline}
                </span>
              </div>
              <p className="font-body text-sm text-text-secondary">
                {milestone.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-border">
        <div>
          <p className="font-body text-xs text-muted-foreground mb-1">
            Investment Range
          </p>
          <p className="font-headline text-lg font-semibold text-foreground">
            {phase.investmentRange}
          </p>
        </div>
        <div>
          <p className="font-body text-xs text-muted-foreground mb-1">
            Expected Impact
          </p>
          <p className="font-headline text-lg font-semibold text-success">
            {phase.expectedImpact}
          </p>
        </div>
      </div>
    </div>
  );
}